import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import ViewAttendance from '../views/ViewAttendance.vue';
import ViewAttendanceEdit from '../views/ViewAttendanceEdit.vue';
import ViewAttendanceMark from '../views/ViewAttendanceMark.vue';
import ViewAttendanceCompany from '../views/ViewAttendanceCompany.vue';
import ViewAttendanceBulkEdit from '../views/ViewAttendanceBulkEdit.vue';
import ViewEmployees from '../views/ViewEmployees.vue';
import ViewRoster from '../views/ViewRoster.vue';
import ViewSchedule from '../views/ViewSchedule.vue';
import ViewLogs from '../views/ViewLogs.vue';
import HomePage from '../views/HomePage.vue';
import ViewInsight from '../views/ViewInsight.vue';
import ViewComplaints from '../views/ViewComplaints.vue';
import LoginPage from '../views/LoginPage.vue';
import ViewLeaves from '../views/ViewLeaves.vue';
import ViewLeavesCalendar from '../views/ViewLeavesCalendar.vue';
import ViewCoverCalendar from '../views/ViewCoverCalendar.vue';
import ViewEditRoster from '../views/ViewEditRoster.vue';
import ViewRosterCreate from '../views/ViewRosterCreate.vue';
import { buildDefaultRosterRoute } from '@/composables/useRosterRouteFilters';
import { buildDefaultAttendanceRoute } from '@/composables/useAttendanceRouteFilters';
import { buildDefaultCalendarRoute } from '@/composables/useCalendarRouteFilters';
import { buildDashboardRoute } from '@/composables/useDashboardRoute';

const routes = [
  {
    path: '/attendance',
    name: 'ViewAttendance',
    redirect: () => buildDefaultAttendanceRoute(),
    meta: { requiresAuth: true, title: 'Attendance - IMAB CORE' }
  },
  {
    path: '/attendance/:companyId/:employeeIds/:filterType/:shiftType/:notLogout/:dateRange/:page',
    name: 'ViewAttendanceFiltered',
    component: ViewAttendance,
    meta: { requiresAuth: true, title: 'Attendance - IMAB CORE' }
  },
  {
    path: '/attendance/:employeeIds/:filterType/:shiftType/:notLogout/:dateRange/:page',
    redirect: to => ({
      name: 'ViewAttendanceFiltered',
      params: { ...to.params, companyId: 'all' },
      query: to.query
    })
  },
  {
    path: '/attendance/:employeeIds/:filterType/:shiftType/:notLogout/:dateRange',
    redirect: to => ({
      name: 'ViewAttendanceFiltered',
      params: { ...to.params, companyId: 'all', page: '1' },
      query: to.query
    })
  },
  {
    path: '/attendance/:employeeIds/:filterType/:dateRange/:page',
    redirect: to => ({
      name: 'ViewAttendanceFiltered',
      params: {
        companyId: 'all',
        employeeIds: to.params.employeeIds,
        filterType: to.params.filterType,
        shiftType: 'all',
        notLogout: 'all',
        dateRange: to.params.dateRange,
        page: to.params.page
      },
      query: to.query
    })
  },
  {
    path: '/attendance/:employeeIds/:filterType/:dateRange',
    redirect: to => ({
      name: 'ViewAttendanceFiltered',
      params: {
        companyId: 'all',
        employeeIds: to.params.employeeIds,
        filterType: to.params.filterType,
        shiftType: 'all',
        notLogout: 'all',
        dateRange: to.params.dateRange,
        page: '1'
      },
      query: to.query
    })
  },
  {
    path: '/edit-attendance/:employeeId/:rosterId/:attendanceId',
    name: 'EditAttendance',
    component: ViewAttendanceEdit,
    meta: { requiresAuth: true, title: 'Edit Attendance - IMAB CORE' }
  },
  {
    path: '/mark-attendance/:rosterId',
    name: 'MarkAttendance',
    component: ViewAttendanceMark,
    meta: { requiresAuth: true, title: 'Mark Attendance - IMAB CORE' }
  },
  {
    path: '/change-attendance-company/:attendanceIds',
    name: 'ChangeAttendanceCompany',
    component: ViewAttendanceCompany,
    meta: { requiresAuth: true, title: 'Change Attendance Company - IMAB CORE' }
  },
  {
    path: '/edit-attendance-bulk/:attendanceIds',
    name: 'EditAttendanceBulk',
    component: ViewAttendanceBulkEdit,
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: 'Edit Attendance Records - IMAB CORE'
    }
  },
  {
    path: '/roster',
    name: 'ViewRoster',
    redirect: () => buildDefaultRosterRoute('roster'),
    meta: { requiresAuth: true, title: 'Roster - IMAB CORE' }
  },
  {
    path: '/roster/:companyId/:employeeIds/:filterType/:markedFilter/:dateRange/:page',
    name: 'ViewRosterFiltered',
    component: ViewRoster,
    meta: { requiresAuth: true, title: 'Roster - IMAB CORE' }
  },
  {
    path: '/roster/:companyId/:employeeIds/:filterType/:markedFilter/:dateRange',
    redirect: to => ({
      name: 'ViewRosterFiltered',
      params: { ...to.params, page: '1' },
      query: to.query
    })
  },
  {
    path: '/roster/:employeeIds/:filterType/:dateRange/:page',
    redirect: to => ({
      name: 'ViewRosterFiltered',
      params: {
        companyId: 'all',
        employeeIds: to.params.employeeIds,
        filterType: to.params.filterType,
        markedFilter: 'all',
        dateRange: to.params.dateRange,
        page: to.params.page
      },
      query: to.query
    })
  },
  {
    path: '/roster/:employeeIds/:filterType/:dateRange',
    redirect: to => ({
      name: 'ViewRosterFiltered',
      params: {
        companyId: 'all',
        employeeIds: to.params.employeeIds,
        filterType: to.params.filterType,
        markedFilter: 'all',
        dateRange: to.params.dateRange,
        page: '1'
      },
      query: to.query
    })
  },
  {
    path: '/edit-roster/:rosterIds',
    name: 'EditRoster',
    component: ViewEditRoster,
    meta: { requiresAuth: true, title: 'Edit Roster - IMAB CORE' }
  },
  {
    path: '/create-roster/:employeeId/:shiftType',
    name: 'CreateRoster',
    component: ViewRosterCreate,
    meta: { requiresAuth: true, title: 'Create Roster - IMAB CORE' }
  },
  {
    path: '/leaves',
    name: 'ViewLeaves',
    redirect: () => buildDefaultRosterRoute('leaves'),
    meta: { requiresAuth: true, title: 'Leaves - IMAB CORE' }
  },
  {
    path: '/leaves/:companyId/:employeeIds/:filterType/:dateRange/:page',
    name: 'ViewLeavesFiltered',
    component: ViewLeaves,
    meta: { requiresAuth: true, title: 'Leaves - IMAB CORE' }
  },
  {
    path: '/leaves/:companyId/:employeeIds/:filterType/:dateRange',
    redirect: to => ({
      name: 'ViewLeavesFiltered',
      params: { ...to.params, page: '1' },
      query: to.query
    })
  },
  {
    path: '/leaves/:employeeIds/:filterType/:dateRange/:page',
    redirect: to => ({
      name: 'ViewLeavesFiltered',
      params: { ...to.params, companyId: 'all' },
      query: to.query
    })
  },
  {
    path: '/leaves/:employeeIds/:filterType/:dateRange',
    redirect: to => ({
      name: 'ViewLeavesFiltered',
      params: { ...to.params, companyId: 'all', page: '1' },
      query: to.query
    })
  },
  {
    path: '/insight',
    name: 'ViewInsight',
    component: ViewInsight,
    meta: { requiresAuth: true, title: 'Insight - IMAB CORE' }
  },
  {
    path: '/calendar',
    name: 'ViewCalendar',
    redirect: () => buildDefaultCalendarRoute('leaves'),
    meta: { requiresAuth: true, title: 'Calendar - IMAB CORE' }
  },
  {
    path: '/calendar/:employeeIds/:month',
    name: 'ViewCalendarFiltered',
    redirect: to => ({
      name: 'ViewLeavesCalendarFiltered',
      params: to.params
    }),
    meta: { requiresAuth: true, title: 'Calendar - IMAB CORE' }
  },
  {
    path: '/leave-calendar',
    name: 'ViewLeavesCalendar',
    redirect: () => buildDefaultCalendarRoute('leaves'),
    meta: { requiresAuth: true, title: 'Leave Calendar - IMAB CORE' }
  },
  {
    path: '/leave-calendar/:employeeIds/:month',
    name: 'ViewLeavesCalendarFiltered',
    component: ViewLeavesCalendar,
    meta: { requiresAuth: true, title: 'Leave Calendar - IMAB CORE' }
  },
  {
    path: '/cover-calendar',
    name: 'ViewCoverCalendar',
    redirect: () => buildDefaultCalendarRoute('covers'),
    meta: { requiresAuth: true, title: 'Cover Calendar - IMAB CORE' }
  },
  {
    path: '/cover-calendar/:employeeIds/:month',
    name: 'ViewCoverCalendarFiltered',
    component: ViewCoverCalendar,
    meta: { requiresAuth: true, title: 'Cover Calendar - IMAB CORE' }
  },
  {
    path: '/complaints',
    name: 'ViewComplaints',
    component: ViewComplaints,
    meta: { requiresAuth: true, title: 'Complaints - IMAB CORE' }
  },
  {
    path: '/complaints/:year-:month/:employeeId/:ticketId',
    name: 'ViewComplaintDetail',
    component: ViewComplaints,
    props: true,
    meta: { requiresAuth: true, title: 'Complaint Detail - IMAB CORE' }
  },
  {
    path: '/complaints/:year-:month',
    name: 'ViewComplaintsByMonth',
    component: ViewComplaints,
    props: true,
    meta: { requiresAuth: true, title: 'Complaints - IMAB CORE' }
  },
  {
    path: '/employees',
    name: 'ViewEmployees',
    component: ViewEmployees,
    meta: { requiresAuth: true, title: 'Employees - IMAB CORE' }
  },
  {
    path: '/logs',
    name: 'ViewLogs',
    component: ViewLogs,
    meta: { requiresAuth: true, title: 'Logs - IMAB CORE' }
  },
  {
    path: '/login',
    name: 'LoginPage',
    component: LoginPage,
    meta: { layout: 'none', public: true, title: 'Login - IMAB CORE' }
  },
  {
    path: '/',
    name: 'HomePage',
    redirect: () => buildDashboardRoute(),
    meta: { requiresAuth: true, title: 'IMAB CORE' }
  },
  {
    path: '/dashboard/:date',
    name: 'DashboardDay',
    component: HomePage,
    meta: { requiresAuth: true, title: 'IMAB CORE' }
  },
  {
    path: '/schedule',
    name: 'ViewSchedule',
    component: ViewSchedule,
    meta: { requiresAuth: true, title: 'Schedule - IMAB CORE' }
  },
  
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Ensure auth is initialized
  if (!authStore.isInitialized) {
    await authStore.initializeAuth();
  }

  document.title = to.meta.title || 'IMAB';

  if (to.matched.some(record => record.meta.requiresAuth) && !authStore.isAuthenticated) {
    console.log('User not authenticated, redirecting to login');
    return next({ name: 'LoginPage' });
  }

  if (to.matched.some(record => record.meta.requiresSuperAdmin) && !authStore.isSuperAdmin) {
    return next({ name: 'ViewAttendance' });
  }

  return next();
});

export default router;
